import { AppImage } from 'src/app/models/image';
import { Injectable } from '@angular/core';
import { ImageLinked } from '../models/image.interface';
import { ApiService } from 'src/app/core/services/api.service';
import { StorageService, UploadFileOptions } from 'src/app/core/services/storage.service';
import { PaginationHelpers } from 'src/app/core/classes/pagination.helpers';
import { environment } from 'src/environments/environment';

import { DefaultTable } from 'src/app/models/table-patterns';
import { FileHelper } from 'src/app/core/helpers/file.helpers';

export class RemoveImageBackgroundParams extends DefaultTable {
  baseColor: { R: number, G: number, B: number } = { R: 255, G: 255, B: 255 };
  smothness = 1.1;
  fator = -0.2;
  constructor(o: Partial<RemoveImageBackgroundParams> = { baseColor: { R: 255, G: 255, B: 255 }, smothness: 1.1, fator: -0.2 }) {
    super(o);
    if (!this.baseColor) {
      this.baseColor = { R: 255, G: 255, B: 255 };
    }
  }
}
@Injectable({ providedIn: 'root' })
export class ImageService {
  static basePath: string = environment.firebase.publicImagePath;
  private _apiPath: string;
  constructor(
    private storage: StorageService,
    private api: ApiService
  ) {
    this._apiPath = environment.api.site;
  }

  static convertToStorageURI(path: string) {
    if (!path) {
      return '';
    }
    return path.split('/').join('%2F') + '?alt=media';
  }

  static getImageFirebaseURI(path: string) {
    return this.basePath + this.convertToStorageURI(path);
  }

  getFileFromComputer(accept?: string): Promise<File> {
    if (!accept) {
      accept = 'image/*';
    }
    return this.storage.getFileFromComputer(accept);
  }

  convertFileToBase64(file: File): Promise<string> {
    return this.storage.convertFileToBase64(file);
  }

  convertBase64ToFile(base64: string, fileName: string): Promise<File> {
    return this.storage.convertBase64ToFile(base64, fileName);
  }



  private create(file: File, options: ImageLinked): Promise<AppImage> {
    return new Promise((resolve, reject) => {
      this.storage.uploadImage(file, options.parent_id)
        .then(path => {
          let url = 'images';
          if (!!options.link) {
            url += '/linked';
          }
          options.path = path;

          this.api.post<AppImage>(url, options)
            .subscribe(
              res => {
                resolve(res.payload);
              },
              err => {
                this.storage.removeImage(path).then(() => {
                  reject(err);
                });

              });

        })
        .catch(err => reject(err));
    });
  }

  private createWithProgress(file: File, options: ImageLinked, uploadOptions?: UploadFileOptions) {


    const _task = this.storage.uploadImageProgress(file, options.parent_id, uploadOptions);

    const _promise: Promise<AppImage> = new Promise((resolve, reject) => {

      _task.done$.subscribe((path) => {

        let url = 'images';
        if (!!options.link) {
          url += '/linked';
        }
        options.path = path;
        options.size = file.size;
        if (!options.name) {
          options.name = file.name;
        }

        if (!options.mime_type) {
          options.mime_type = file.type;
        }

        this.api.post<AppImage>(url, options)
          .subscribe(
            res => {
              resolve(res.payload);
            },
            err => {
              this.storage.removeImage(path).then(() => {
                reject(err);
              });
            });
      });

    });

    return {
      task: _task,
      result: _promise
    };
  }

  createImage(file: File, options: ImageLinked): Promise<AppImage> {
    return this.create(file, options);
  }

  createWikiImage(wikiId: number, file: File) {

    return this.storage.uploadWikiImage(file, wikiId)
      .then(path => {
        return `${environment.firebase.publicImagePath}${encodeURIComponent(path)}?alt=media`;
      });
  }

  createImageWithProgress(file: File, options: ImageLinked, uploadOptions?: UploadFileOptions) {
    return this.createWithProgress(file, options, uploadOptions);
  }

  getUrl(path: string, size?: '48' | '64' | '128' | '256' | '512' | 'original'): Promise<string> {
    return new Promise((resolve, reject) => {
      let url = path;
      if (url.startsWith('assets')) {
        return resolve(url);
      }

      if (!path.startsWith('http') && size) {
        const pathArray = path.split('/');
        const name = pathArray.pop();
        const filePath = pathArray.join('/');
        const fileName = name.split('.').shift();
        const ext = name.split('.').pop();
        if (size == 'original') {
          url = url = `${filePath}/${fileName}.${ext}`;
        } else {
          url = `${filePath}/${fileName}@${size}.${ext}`;
        }
      }


      if (url.startsWith('https://graph.facebook.com/') && url.endsWith('/picture')) {
        url += `?width=${size ? size : 500}&height=${size ? size : 500}`;
      }

      if (url.toLowerCase().startsWith('documents/')) {
        this.storage.getPublicURL(url)
          .then(data => resolve(data))
          .catch(error => {
            // if (!environment.production) {
            //   console.warn('Error resolving image url');
            //   console.error(error);
            // }
            reject(error);
          });
        return;
      }

      const res = FileHelper.getImagePublicURL(url);
      resolve(res)

      // this.storage.getPublicURL(url)
      //   .then(data => resolve(data))
      //   .catch(error => {
      //     // if (!environment.production) {
      //     //   console.warn('Error resolving image url');
      //     //   console.error(error);
      //     // }
      //     reject(error);
      //   });

    });


  }

  getEntityImages(entityID: number, page = 1, limit = 20, totalize = false) {
    const options = PaginationHelpers.getGetOptions(page, limit, totalize);
    return this.api.get<AppImage[]>(`entity/${entityID}/pictures`, options);
  }

  removeBackground(
    srcURL: string,
    params: RemoveImageBackgroundParams): Promise<HTMLCanvasElement> {
    return new Promise((resolve, reject) => {

      const img = new Image();
      img.onload = (imageLoadEvent) => {

        const canvas: HTMLCanvasElement = document.createElement('CANVAS') as HTMLCanvasElement;
        canvas.width = (imageLoadEvent.target as any).naturalWidth;
        canvas.height = (imageLoadEvent.target as any).naturalHeight;

        const context: CanvasRenderingContext2D = canvas.getContext('2d');
        try { context.imageSmoothingEnabled = false; } catch (e) { (context as any).msImageSmoothingEnabled = false; }
        context.drawImage(img, 0, 0, canvas.width, canvas.height);


        const imgData = context.getImageData(0, 0, canvas.width, canvas.height);
        for (let i = 0; i < imgData.data.length; i += 4) {
          let datai = imgData.data[i];
          let datai1 = imgData.data[i + 1];
          let datai2 = imgData.data[i + 2];

          datai = params.smothness *
            imgData.data[i] + 0 *
            imgData.data[i + 1] + 0 *
            imgData.data[i + 2] + 0 *
            imgData.data[i + 3] +
            params.fator;

          datai1 = 0 *
            imgData.data[i] + params.smothness *
            imgData.data[i + 1] + 0 *
            imgData.data[i + 2] + 0 *
            imgData.data[i + 3] +
            params.fator;

          datai2 = 0 *
            imgData.data[i] + 0 *
            imgData.data[i + 1] + params.smothness *
            imgData.data[i + 2] + 0 *
            imgData.data[i + 3] + params.fator;

          imgData.data[i] = datai;
          imgData.data[i + 1] = datai1;
          imgData.data[i + 2] = datai2;
        }

        // // cor branca #FFFFFF (R=255, G=255, B=255) => transparente (Alfa=0)
        for (
          let i = 0; i < imgData.data.length; i += 4) {
          if (imgData.data[i] == params.baseColor.R &&
            imgData.data[i + 1] == params.baseColor.G &&
            imgData.data[i + 2] == params.baseColor.B
          ) { imgData.data[i + 3] = 0; }
        }

        context.putImageData(imgData, 0, 0);

        resolve(canvas);
      };
      img.onerror = (error) => {
        reject(error);
      };
      img.src = srcURL;
    });

  }

  getAssetImageUrl(imagePath: string) {
    return this._apiPath + '/' + imagePath;
  }

  async getAssetImageAsBlob(imagePath: string): Promise<{
    path: string;
    file: Blob
  }> {
    return this.storage.getFileAsBlob(this.getAssetImageUrl(imagePath));
  }

  async getImageAsBlob(imagePath: string): Promise<{
    path: string;
    file: Blob
  }> {
    let src = imagePath;
    if (imagePath.startsWith('asset')) {
      src = this.getAssetImageUrl(imagePath);
    } else if (!imagePath.startsWith('http')) {
      src = await this.getUrl(imagePath);
    }

    return this.storage.getFileAsBlob(src);
  }

  async getImageAsBase64(imagePath: string, size?: 'original' | '48' | '64' | '128' | '256' | '512'): Promise<{
    path: string;
    file: string
  }> {
    let src = imagePath;
    if (imagePath.startsWith('asset')) {
      src = this.getAssetImageUrl(imagePath);
    } else if (!imagePath.startsWith('http')) {
      src = await this.getUrl(imagePath, size);
    }

    return this.storage.getFileAsBase64(src);
  }

  getAsBase64(path: string) {
    return this.storage.getFileAsBase64(path);
  }


  calculateAspectRatio(width: number, height: number) {
    const gdc = (a: number, b: number) => {
      if (b === 0) {
        return a;
      }
      return gdc(b, a % b);
    }

    const gcd = gdc(width, height);
    return {
      width: width / gcd,
      height: height / gcd,
      text: `${width / gcd}x${height / gcd}`,
      gdc
    }
  }


}
